(() => {
  const ROOT_SELECTOR = '.shopify-section--main-collection';
  const DENSITY_KEY = 'forgetech:collection-density-v2';
  const instances = new WeakMap();

  class CollectionBrowse {
    constructor(root) {
      this.root = root;
      this.collection = root.querySelector('.collection');
      this.productList = root.querySelector('product-list.product-list');
      this.sentinel = root.querySelector('[data-collection-infinite-sentinel]');
      this.loadButton = root.querySelector('[data-collection-load-more]');
      this.status = root.querySelector('[data-collection-infinite-status]');
      this.mobileFilterTrigger = root.querySelector('[data-collection-filter-trigger]');
      this.floatingFilter = root.querySelector('[data-collection-floating-filter]');
      this.footer = document.querySelector('.footer');
      this.mobileFilterTriggerPassed = false;
      this.footerReached = false;
      this.loading = false;
      this.complete = false;
      this.nextUrl = this.findNextUrl(root);

      if (!this.collection || !this.productList || !this.sentinel) return;

      this.onClick = this.onClick.bind(this);
      this.onWindowScroll = this.onWindowScroll.bind(this);
      this.root.addEventListener('click', this.onClick);
      this.setDensity(this.readDensity());
      this.setupFloatingFilter();
      this.setupInfiniteLoad();
    }

    destroy() {
      this.observer?.disconnect();
      this.floatingFilterObserver?.disconnect();
      this.root.removeEventListener('click', this.onClick);
      window.removeEventListener('scroll', this.onWindowScroll);
    }

    setupFloatingFilter() {
      if (!this.mobileFilterTrigger || !this.floatingFilter || !this.footer || !('IntersectionObserver' in window)) {
        return;
      }

      this.floatingFilterObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.mobileFilterTrigger) {
            this.mobileFilterTriggerPassed = entry.boundingClientRect.bottom < 0;
          }

          if (entry.target === this.footer) {
            this.footerReached = entry.isIntersecting;
          }
        });

        this.updateFloatingFilter();
      });

      this.floatingFilterObserver.observe(this.mobileFilterTrigger);
      this.floatingFilterObserver.observe(this.footer);
    }

    updateFloatingFilter() {
      const isVisible = this.mobileFilterTriggerPassed && !this.footerReached;
      const floatingButton = this.floatingFilter?.querySelector('button');

      this.floatingFilter?.classList.toggle('is-visible', isVisible);
      this.floatingFilter?.setAttribute('aria-hidden', String(!isVisible));

      if (floatingButton) floatingButton.tabIndex = isVisible ? 0 : -1;
    }

    readDensity() {
      try {
        return sessionStorage.getItem(DENSITY_KEY) || '2';
      } catch (_) {
        return '2';
      }
    }

    setDensity(value) {
      const density = ['1', '2', '3'].includes(String(value)) ? String(value) : '2';
      this.collection.dataset.mobileDensity = density;
      this.root.querySelectorAll('[data-collection-density]').forEach((button) => {
        const active = button.dataset.collectionDensity === density;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });

      try {
        sessionStorage.setItem(DENSITY_KEY, density);
      } catch (_) {
        // Density still works for this view when storage is unavailable.
      }
    }

    onClick(event) {
      const densityButton = event.target.closest('[data-collection-density]');
      if (densityButton && this.root.contains(densityButton)) {
        this.setDensity(densityButton.dataset.collectionDensity);
        return;
      }

      const loadButton = event.target.closest('[data-collection-load-more]');
      if (loadButton && this.root.contains(loadButton)) this.loadNext();
    }

    findNextUrl(scope) {
      return scope.querySelector('.collection-pagination-source a[rel="next"], .collection__pagination a[rel="next"]')?.href || '';
    }

    setupInfiniteLoad() {
      if (!this.nextUrl) {
        this.finish();
        return;
      }

      this.loadButton.hidden = true;
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      if (!('IntersectionObserver' in window)) {
        this.loadButton.hidden = false;
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
        },
        { rootMargin: '1200px 0px' }
      );
      this.observer.observe(this.sentinel);
      requestAnimationFrame(() => this.onWindowScroll());
    }

    onWindowScroll() {
      if (this.loading || this.complete || !this.nextUrl) return;
      if (this.sentinel.getBoundingClientRect().top < window.innerHeight + 1200) this.loadNext();
    }

    existingHandles() {
      return new Set(
        Array.from(this.productList.querySelectorAll('product-card[handle]'))
          .map((card) => card.getAttribute('handle'))
          .filter(Boolean)
      );
    }

    async loadNext() {
      if (this.loading || this.complete || !this.nextUrl) return;

      this.loading = true;
      this.productList.setAttribute('aria-busy', 'true');
      this.sentinel.classList.add('is-loading');
      this.loadButton.disabled = true;
      this.loadButton.textContent = 'Loading';
      this.status.textContent = 'Loading more products.';

      try {
        const response = await fetch(this.nextUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);

        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextRoot = page.querySelector(ROOT_SELECTOR);
        const nextList = nextRoot?.querySelector('product-list.product-list');
        if (!nextList) throw new Error('Collection products were missing from the response.');

        const known = this.existingHandles();
        const fragment = document.createDocumentFragment();
        let appended = 0;

        Array.from(nextList.children).forEach((item) => {
          const card = item.matches?.('product-card') ? item : item.querySelector?.('product-card');
          const handle = card?.getAttribute('handle');
          if (!card || (handle && known.has(handle))) return;
          if (handle) known.add(handle);
          item.classList.add('ft-newly-loaded');
          fragment.appendChild(item);
          appended += 1;
        });

        this.productList.appendChild(fragment);
        this.nextUrl = this.findNextUrl(nextRoot);
        requestAnimationFrame(() => {
          this.productList.querySelectorAll('.ft-newly-loaded').forEach((item) => {
            item.classList.remove('ft-newly-loaded');
          });
        });

        this.status.textContent = appended ? 'More products loaded.' : 'Loading the next products.';
        if (!this.nextUrl) this.finish();
      } catch (error) {
        this.observer?.disconnect();
        this.loadButton.hidden = false;
        this.loadButton.disabled = false;
        this.loadButton.textContent = 'Try again';
        this.status.textContent = 'More products could not be loaded. Use Try again.';
      } finally {
        this.loading = false;
        this.productList.setAttribute('aria-busy', 'false');
        this.sentinel.classList.remove('is-loading');

        if (!this.complete && this.loadButton.textContent !== 'Try again') {
          this.loadButton.disabled = false;
          this.loadButton.textContent = 'Load more';
          this.loadButton.hidden = true;
          requestAnimationFrame(() => this.onWindowScroll());
        }
      }
    }

    finish() {
      this.complete = true;
      this.nextUrl = '';
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onWindowScroll);
      this.sentinel.classList.add('is-complete');
      this.loadButton.hidden = true;
      this.status.textContent = '';
    }
  }

  function initRoot(root) {
    const previous = instances.get(root);
    previous?.destroy();
    instances.set(root, new CollectionBrowse(root));
  }

  function initAll(scope = document) {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  function handleDetachedMobileSort(event) {
    const popover = event.target.closest?.('[data-collection-sort-section]');
    if (!popover || popover.parentElement !== document.body) return;

    const sectionId = popover.dataset.collectionSortSection;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', event.detail.value);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.delete('page');

    document.dispatchEvent(new CustomEvent('facet:update', {
      detail: { url }
    }));
  }

  const mutationObserver = new MutationObserver((records) => {
    const roots = new Set();
    records.forEach((record) => {
      const directRoot = record.target instanceof Element && record.target.matches(ROOT_SELECTOR)
        ? record.target
        : null;
      if (directRoot) roots.add(directRoot);
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(ROOT_SELECTOR)) roots.add(node);
        node.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.add(root));
        if (node.matches('.collection') || node.querySelector?.('.collection')) {
          const parentRoot = node.closest(ROOT_SELECTOR);
          if (parentRoot) roots.add(parentRoot);
        }
      });
    });
    roots.forEach(initRoot);
  });

  document.addEventListener('DOMContentLoaded', () => {
    initAll();
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }, { once: true });

  document.addEventListener('listbox:change', handleDetachedMobileSort);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
